import { Img } from "@/components/site/Img";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, MessageCircle, Facebook, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/track";
import { useSmsHref } from "@/components/site/ContactButtons";
import { FB_USERNAME, SMS_DRAFTS } from "@/lib/contact";

const FB_PAGE_URL = `https://www.facebook.com/${FB_USERNAME}`;

const inputCls =
  "w-full px-4 py-3 bg-white/5 border border-[#3A3835] text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#C8B387] transition-colors";
const labelCls = "block text-[10px] tracking-[0.24em] uppercase text-[#B8B3AA] mb-1.5";

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const conversionFired = useRef(false);

  useEffect(() => {
    if (!sent || conversionFired.current) return;
    conversionFired.current = true;
    const sendTo = process.env.REACT_APP_GADS_CONTACT_CONVERSION;
    if (sendTo && typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "conversion", { send_to: sendTo });
    }
  }, [sent]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          message: `${form.phone ? `Phone: ${form.phone}\n` : ""}${form.message}`,
        }),
      });
      if (!res.ok) throw new Error("failed");
      track("custom_inquiry_click", "Contact form");
      setSent(true);
      toast.success("Sent! Chris will get back to you, usually within a day or two.");
    } catch {
      toast.error("Could not send right now. Please call (678) 769-9652.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="contact-form-success"
        className="border border-[#C8B387]/50 bg-[#26241F] p-6 text-sm text-[#D8D2C4] leading-relaxed"
      >
        Your message is on its way to Chris. He will call or email you back,
        usually within a day or two.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-3 max-w-xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="contact-name" className={labelCls}>Your name</label>
          <input
            id="contact-name"
            autoComplete="name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="First and last name"
            data-testid="contact-form-name"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="contact-phone" className={labelCls}>Phone (optional)</label>
          <input
            id="contact-phone"
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="(678) 555-0123"
            data-testid="contact-form-phone"
            className={inputCls}
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-email" className={labelCls}>Email</label>
        <input
          id="contact-email"
          required
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          data-testid="contact-form-email"
          className={inputCls}
        />
      </div>
      <div>
        <label htmlFor="contact-message" className={labelCls}>Message</label>
        <textarea
          id="contact-message"
          required
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Tell Chris about the piece you saw or the custom fountain you have in mind"
          data-testid="contact-form-message"
          className={`${inputCls} resize-none`}
        />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="contact-form-submit"
        className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#7A6547] text-[#F9F6F0] text-[11px] tracking-[0.22em] uppercase hover:bg-[#5E4D35] transition-colors disabled:opacity-60"
      >
        <Send size={13} strokeWidth={1.5} />
        {busy ? "Sending..." : "Send To Chris"}
      </button>
    </form>
  );
};

export default function Contact() {
  const generalSms = useSmsHref(SMS_DRAFTS.general);
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#1F1E1C] text-[#F9F6F0] pt-10 md:pt-14 pb-10 md:pb-12"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="max-w-3xl lg:col-span-7">
            <div className="section-eyebrow mb-5 !text-[#C8B387]">
              <span>Get In Touch</span>
            </div>
            <h2
              data-testid="contact-heading"
              className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6"
            >
              Let&rsquo;s create your garden&rsquo;s{" "}
              <em className="not-italic text-[#C8B387]">centerpiece.</em>
            </h2>
            <p className="text-[#D8D2C4] text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Call, text, or fill out the form below about a piece you&rsquo;ve
              seen here or a custom idea. It goes straight to Chris, and he
              usually responds within one business day.
            </p>
            <ContactForm />
          </div>
          <Link
            to="/builds#the-majestic-lions"
            data-testid="contact-latest-build"
            className="hidden lg:block lg:col-span-5 group self-stretch"
          >
            <div className="relative h-full min-h-[560px] overflow-hidden bg-[#26241F]">
              <Img
                src="/images/majestic-lions-setting.jpg"
                alt="Chris setting the lions on the newest Majestic fountain in a stone-walled driveway circle"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1F1E1C]/90 to-transparent pt-20 pb-6 px-6">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C8B387] mb-2">Latest build</div>
                <div className="font-serif text-2xl text-[#F9F6F0] font-light leading-tight">Setting the lions on the newest Majestic.</div>
                <span className="mt-3 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#C8B387] group-hover:text-[#F9F6F0] transition-colors">
                  See how it was built <ArrowUpRight size={13} strokeWidth={1.5} />
                </span>
              </div>
            </div>
          </Link>
        </div>

        <div className="mt-12 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
          <a href="tel:+16787699652" data-testid="contact-phone" className="flex items-start gap-4 group">
            <Phone size={20} strokeWidth={1.25} className="text-[#C8B387] mt-1 flex-shrink-0" />
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#C8B387] mb-1">Phone</div>
              <div className="text-[#F9F6F0] group-hover:text-[#C8B387] transition-colors">
                (678) 769-9652
              </div>
            </div>
          </a>

          <a
            href={generalSms}
            onClick={() => track("text_click", "Contact grid")}
            data-testid="contact-messenger-link"
            aria-label="Text Chris at (678) 769-9652"
            className="flex items-start gap-4 group"
          >
            <MessageCircle size={20} strokeWidth={1.25} className="text-[#C8B387] mt-1 flex-shrink-0" />
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#C8B387] mb-1">Text</div>
              <div className="text-[#F9F6F0] group-hover:text-[#C8B387] transition-colors inline-flex items-center gap-1.5">
                Text us directly
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </div>
            </div>
          </a>

          <a
            href={FB_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-facebook-link"
            className="flex items-start gap-4 group"
          >
            <Facebook size={20} strokeWidth={1.25} className="text-[#C8B387] mt-1 flex-shrink-0" />
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#C8B387] mb-1">Facebook</div>
              <div className="text-[#F9F6F0] group-hover:text-[#C8B387] transition-colors inline-flex items-center gap-1.5">
                Garden Fountains Yard Sculptures
                <ArrowUpRight size={13} strokeWidth={1.5} />
              </div>
            </div>
          </a>

          <div data-testid="contact-location" className="flex items-start gap-4">
            <MapPin size={20} strokeWidth={1.25} className="text-[#C8B387] mt-1 flex-shrink-0" />
            <div>
              <div className="text-[11px] tracking-[0.28em] uppercase text-[#C8B387] mb-1">Location</div>
              <div className="text-[#F9F6F0]">Villa Rica, Georgia</div>
              <div data-testid="contact-service-area" className="text-sm text-[#C8B387] mt-1">
                We will come to you &mdash; repairs, cleanings, and deliveries are available within the United States for a small fee.
              </div>
              <div data-testid="contact-yard-visits-note" className="text-sm text-[#E8DFC9] mt-2">
                Yard visits are welcome&mdash;please call before coming.
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
